import React, { useEffect, useRef, useState } from 'react';
import { ChevronRight, MessageCircle, Send, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supportApi } from '../api/supportApi';
import { useAuth } from '../context/AuthContext';
import { SUPPORT_OPEN_EVENT } from '../lib/supportBus';
import {
  connectGuestSocket,
  disconnectGuestSocket,
  getGuestSocket,
} from '../socket/guestSocketClient';

const SUPPORT_NAME = 'GIGGS Support';
const SUPPORT_AVATAR = 'GS';
const SESSION_KEY = 'giggs_support_session';

const now = () => new Date();
const fmtTime = (value) =>
  (value instanceof Date ? value : new Date(value)).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });
const isEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((value || '').trim());
const uid = () =>
  (typeof crypto !== 'undefined' && crypto.randomUUID?.()) ||
  `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const messagesRouteFor = (role) =>
  ({
    client: '/client/dashboard',
    provider: '/provider/messages',
    admin: '/admin/messages',
  }[role] || '/login');

const loadSession = () => {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const saveSession = (session) => {
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  } catch {
    // Ignore storage restrictions in private browsing or quota errors.
  }
};

const clearSession = () => {
  try {
    localStorage.removeItem(SESSION_KEY);
  } catch {
    // No-op.
  }
};

const mapRaw = (message) => ({
  _id: String(message._id),
  from: message.fromGuest ? 'me' : 'support',
  text: message.content,
  at: message.createdAt ? new Date(message.createdAt) : now(),
  clientMessageId: message.clientMessageId || null,
});

const mapSerialized = (message) => ({
  _id: String(message._id),
  from: message.from,
  text: message.text,
  at: message.at ? new Date(message.at) : now(),
  clientMessageId: message.clientMessageId || null,
});

const SupportChat = ({
  onSubmit,
  endpoint = '/api/support/contact',
  position = 'right',
  greeting = "Hi there! 👋 Have a question? Send us a message and our team will get back to you shortly.",
}) => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState(isAuthenticated ? 'chat' : 'collect');
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [thread, setThread] = useState([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [agentTyping, setAgentTyping] = useState(false);
  const [error, setError] = useState('');

  const bodyRef = useRef(null);
  const inputRef = useRef(null);
  const sessionRef = useRef(null);
  const startedRef = useRef(false);
  const typingStopTimer = useRef(null);
  const agentTypingTimer = useRef(null);

  const reconcile = (incoming) => {
    setThread((current) => {
      if (incoming.clientMessageId) {
        const index = current.findIndex(
          (message) =>
            message.clientMessageId && message.clientMessageId === incoming.clientMessageId
        );
        if (index !== -1) {
          const next = [...current];
          next[index] = {
            ...next[index],
            _id: incoming._id,
            text: incoming.text,
            at: incoming.at,
            from: incoming.from,
            pending: false,
          };
          return next;
        }
      }

      if (
        incoming._id &&
        current.some((message) => message._id && String(message._id) === String(incoming._id))
      ) {
        return current;
      }

      return [
        ...current,
        {
          id: String(incoming._id || uid()),
          _id: incoming._id || null,
          from: incoming.from,
          text: incoming.text,
          at: incoming.at,
          clientMessageId: incoming.clientMessageId || null,
          pending: false,
        },
      ];
    });
  };

  const showAgentTyping = () => {
    setAgentTyping(true);
    clearTimeout(agentTypingTimer.current);
    agentTypingTimer.current = setTimeout(() => setAgentTyping(false), 4000);
  };

  const attachGuestListeners = (socket) => {
    if (!socket || socket.__giggsBound) return;
    socket.__giggsBound = true;

    socket.on('message:new', (message) => {
      try {
        reconcile(mapRaw(message));
      } catch (err) {
        console.error('[support] message:new map failed', err);
      }
    });
    socket.on('typing:start', showAgentTyping);
    socket.on('typing:stop', () => {
      clearTimeout(agentTypingTimer.current);
      setAgentTyping(false);
    });
    socket.on('support:error', (payload) =>
      console.warn('[support] server error:', payload?.message)
    );
  };

  const resumeGuestSession = async (session) => {
    sessionRef.current = session;
    startedRef.current = true;
    setName((current) => current || session.name || '');
    setEmail((current) => current || session.email || '');
    setPhase('chat');

    const socket = connectGuestSocket(session.token);
    attachGuestListeners(socket);

    try {
      const response = await supportApi.getMessages(session.conversationId, session.token);
      const history = (response.data?.data || []).map(mapSerialized);
      setThread((current) => {
        const seen = new Set(
          current.filter((message) => message._id).map((message) => String(message._id))
        );
        const merged = [...current];
        history.forEach((message) => {
          if (!seen.has(String(message._id))) merged.push({ id: message._id, ...message });
        });
        return merged;
      });
    } catch (err) {
      console.warn('[support] resume failed:', err.message);
      clearSession();
      sessionRef.current = null;
      startedRef.current = false;
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      setName((current) => current || user?.name || '');
      setEmail((current) => current || user?.email || '');
      setPhase((current) => (current === 'collect' ? 'chat' : current));
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener(SUPPORT_OPEN_EVENT, onOpen);
    return () => window.removeEventListener(SUPPORT_OPEN_EVENT, onOpen);
  }, []);

  useEffect(() => {
    if (!open || isAuthenticated || startedRef.current) return;
    const session = loadSession();
    if (session?.token && session?.conversationId) resumeGuestSession(session);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isAuthenticated]);

  useEffect(
    () => () => {
      clearTimeout(typingStopTimer.current);
      clearTimeout(agentTypingTimer.current);
      disconnectGuestSocket();
    },
    []
  );

  useEffect(() => {
    if (open && phase === 'chat' && thread.length === 0) {
      const hello =
        isAuthenticated && user?.name
          ? `Hi ${user.name.split(' ')[0]}! 👋 How can the GIGGS team help you today?`
          : greeting;
      setThread([{ id: uid(), from: 'support', text: hello, at: now() }]);
    }
  }, [open, phase]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    bodyRef.current?.scrollTo({
      top: bodyRef.current.scrollHeight,
      behavior: 'smooth',
    });
  }, [thread, agentTyping, phase, open]);

  useEffect(() => {
    if (!open) return undefined;
    const timer = setTimeout(() => inputRef.current?.focus(), 220);
    return () => clearTimeout(timer);
  }, [open, phase]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => event.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const pushSupport = (text, delay = 750) => {
    setAgentTyping(true);
    setTimeout(() => {
      setAgentTyping(false);
      setThread((current) => [
        ...current,
        { id: uid(), from: 'support', text, at: now() },
      ]);
    }, delay);
  };

  const notifyOnSubmit = (message) => {
    if (typeof onSubmit !== 'function') return;
    try {
      Promise.resolve(
        onSubmit({
          name: name.trim(),
          email: email.trim(),
          message,
          history: thread.map(({ from, text }) => ({ from, text })),
          user: user ? { id: user._id, role: user.role } : null,
        })
      ).catch(() => {});
    } catch {
      // Optional integration; do not block support chat.
    }
  };

  const deliverAuthed = async (message) => {
    if (typeof onSubmit === 'function') return onSubmit({ message, user });
    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message, user: user ? user._id : null }),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
    } catch (err) {
      console.warn('[SupportChat] simulated delivery:', err.message);
    }
    return { ok: true };
  };

  const startChat = (event) => {
    event?.preventDefault();
    setError('');
    if (!name.trim()) return setError('Please enter your name.');
    if (!isEmail(email)) return setError('Please enter a valid email.');
    setPhase('chat');
  };

  const startSupport = async (text) => {
    setSending(true);
    setError('');
    try {
      const response = await supportApi.start({
        name: name.trim(),
        email: email.trim(),
        message: text,
      });
      const data = response.data?.data;
      if (!data?.token) throw new Error('Could not start support chat.');

      const session = {
        token: data.token,
        guestId: data.guestId,
        conversationId: data.conversationId,
        name: name.trim(),
        email: email.trim(),
      };
      sessionRef.current = session;
      saveSession(session);

      const socket = connectGuestSocket(data.token);
      attachGuestListeners(socket);
      startedRef.current = true;
      (data.messages || []).map(mapSerialized).forEach(reconcile);
      notifyOnSubmit(text);
    } catch (err) {
      console.error('[support] start failed:', err);
      setError(
        err.response?.data?.message ||
          'Something went wrong starting the chat. Please try again.'
      );
      setThread((current) =>
        current.filter((message) => message.text !== text || message.from !== 'me')
      );
    } finally {
      setSending(false);
    }
  };

  const sendGuestMessage = async (text) => {
    const session = sessionRef.current;
    if (!session) return;

    const clientMessageId = uid();
    setThread((current) => [
      ...current,
      {
        id: clientMessageId,
        _id: null,
        from: 'me',
        text,
        at: now(),
        clientMessageId,
        pending: true,
      },
    ]);

    const socket = getGuestSocket();
    if (socket?.connected) {
      socket.emit(
        'support:guest:send',
        { content: text, clientMessageId },
        (ack) => {
          if (ack?.ok && ack.raw) reconcile(mapRaw(ack.raw));
          else if (!ack?.ok) console.warn('[support] send ack error:', ack?.error);
        }
      );
      notifyOnSubmit(text);
      return;
    }

    try {
      const response = await supportApi.sendMessage(
        session.conversationId,
        session.token,
        { content: text, clientMessageId }
      );
      if (response.data?.data) reconcile(mapSerialized(response.data.data));
      notifyOnSubmit(text);
    } catch (err) {
      console.error('[support] REST send failed:', err);
      setThread((current) =>
        current.map((message) =>
          message.clientMessageId === clientMessageId
            ? { ...message, pending: false, failed: true }
            : message
        )
      );
    }
  };

  const stopGuestTyping = () => {
    clearTimeout(typingStopTimer.current);
    const socket = getGuestSocket();
    if (socket?.connected && startedRef.current && !isAuthenticated) {
      socket.emit('support:guest:typing:stop');
    }
  };

  const send = async (event) => {
    event?.preventDefault();
    const text = draft.trim();
    if (!text || sending) return;
    setDraft('');
    stopGuestTyping();

    if (isAuthenticated) {
      setThread((current) => [
        ...current,
        { id: uid(), from: 'me', text, at: now() },
      ]);
      setSending(true);
      try {
        await deliverAuthed(text);
        pushSupport(
          'Thanks! A team member will reply here shortly — you can also open your account below.'
        );
      } catch (err) {
        pushSupport("Hmm, that didn't go through. Please try again in a moment.");
        console.error('[SupportChat] deliver failed:', err);
      } finally {
        setSending(false);
      }
      return;
    }

    if (!startedRef.current) {
      setThread((current) => [
        ...current,
        { id: uid(), from: 'me', text, at: now() },
      ]);
      await startSupport(text);
    } else {
      await sendGuestMessage(text);
    }
  };

  const emitGuestTyping = () => {
    if (isAuthenticated || !startedRef.current) return;
    const socket = getGuestSocket();
    if (!socket?.connected) return;
    socket.emit('support:guest:typing:start');
    clearTimeout(typingStopTimer.current);
    typingStopTimer.current = setTimeout(() => {
      socket.emit('support:guest:typing:stop');
    }, 2000);
  };

  const onDraftChange = (event) => {
    setDraft(event.target.value);
    emitGuestTyping();
  };

  const goToMessages = () => {
    setOpen(false);
    navigate(messagesRouteFor(user?.role));
  };

  const onComposerKey = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      send();
    }
  };

  const sideClass = position === 'left' ? 'left-4 sm:left-6' : 'right-4 sm:right-6';
  const fieldClass =
    'box-border w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-green-600 focus:ring-2 focus:ring-green-100';

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-label={open ? 'Close support chat' : 'Open support chat'}
        aria-expanded={open}
        className={`fixed bottom-4 z-[76] flex h-14 w-14 cursor-pointer items-center justify-center rounded-full border-0 bg-green-700 text-white shadow-xl transition hover:bg-green-800 sm:bottom-6 ${sideClass}`}
      >
        {open ? <X size={23} /> : <MessageCircle size={23} />}
        {!open && (
          <span className="absolute right-0 top-0 h-3.5 w-3.5 rounded-full border-2 border-white bg-green-300 shadow-sm" />
        )}
      </button>

      {open && (
        <section
          className={`fixed bottom-20 z-[75] flex max-h-[calc(100vh-7rem)] w-[calc(100vw-2rem)] max-w-sm flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl sm:bottom-24 ${sideClass}`}
          role="dialog"
          aria-label="GIGGS Support chat"
        >
          <header className="flex items-center gap-3 bg-slate-950 px-5 py-4 text-white">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-green-600 text-xs font-black text-white">
              {SUPPORT_AVATAR}
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-black text-white">{SUPPORT_NAME}</div>
              <div className="mt-1 flex items-center gap-2 text-xs text-slate-300">
                <span className="h-2 w-2 rounded-full bg-green-400" /> We're here 24/7
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close support chat"
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-xl border border-white/10 bg-white/5 text-white transition hover:bg-white/10"
            >
              <X size={18} />
            </button>
          </header>

          {phase === 'collect' ? (
            <form onSubmit={startChat} className="space-y-4 p-5 sm:p-6">
              <p className="m-0 text-sm leading-6 text-slate-600">
                Tell us who you are and we'll get you to the right place.
              </p>
              <label className="block">
                <span className="mb-1.5 block text-xs font-bold text-slate-700">Name</span>
                <input
                  ref={inputRef}
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  placeholder="Your name"
                  autoComplete="name"
                  className={fieldClass}
                />
              </label>
              <label className="block">
                <span className="mb-1.5 block text-xs font-bold text-slate-700">Email</span>
                <input
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="you@example.com"
                  autoComplete="email"
                  className={fieldClass}
                />
              </label>
              {error && (
                <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-xs font-semibold text-red-700">
                  {error}
                </div>
              )}
              <button
                type="submit"
                className="inline-flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl border-0 bg-green-700 px-4 py-3 text-sm font-bold text-white transition hover:bg-green-800"
              >
                Start chat <ChevronRight size={16} />
              </button>
            </form>
          ) : (
            <>
              <div ref={bodyRef} className="min-h-64 flex-1 space-y-3 overflow-y-auto bg-slate-50 p-4 sm:p-5">
                {thread.map((message, index) => {
                  const own = message.from === 'me';
                  return (
                    <div
                      key={message.id || message._id || message.clientMessageId || index}
                      className={`flex items-end gap-2 ${own ? 'justify-end' : 'justify-start'}`}
                    >
                      {!own && (
                        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-slate-950 text-[9px] font-black text-white">
                          {SUPPORT_AVATAR}
                        </span>
                      )}
                      <div
                        className={`max-w-[82%] rounded-2xl px-3.5 py-2.5 text-sm leading-5 shadow-sm ${
                          own
                            ? 'rounded-br-md bg-green-700 text-white'
                            : 'rounded-bl-md border border-slate-200 bg-white text-slate-800'
                        } ${message.pending ? 'opacity-65' : ''}`}
                      >
                        <p className="m-0 whitespace-pre-wrap break-words">{message.text}</p>
                        <span className={`mt-1 block text-[10px] ${own ? 'text-green-100' : 'text-slate-400'}`}>
                          {fmtTime(message.at)}
                          {message.pending && ' · sending…'}
                          {message.failed && ' · failed'}
                        </span>
                      </div>
                    </div>
                  );
                })}

                {agentTyping && (
                  <div className="flex items-end gap-2">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-slate-950 text-[9px] font-black text-white">
                      {SUPPORT_AVATAR}
                    </span>
                    <div className="flex items-center gap-1 rounded-2xl rounded-bl-md border border-slate-200 bg-white px-4 py-3 shadow-sm">
                      <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-400" />
                      <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-400" />
                      <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-400" />
                    </div>
                  </div>
                )}

                {isAuthenticated && (
                  <button
                    type="button"
                    onClick={goToMessages}
                    className="mx-auto flex cursor-pointer items-center gap-1.5 rounded-full border border-green-200 bg-white px-3 py-2 text-xs font-bold text-green-700 shadow-sm"
                  >
                    Open my account <ChevronRight size={14} />
                  </button>
                )}

                {error && (
                  <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-xs font-semibold text-red-700">
                    {error}
                  </div>
                )}
              </div>

              <form onSubmit={send} className="flex items-end gap-2 border-t border-slate-100 bg-white p-4">
                <textarea
                  ref={inputRef}
                  rows={1}
                  value={draft}
                  onChange={onDraftChange}
                  onKeyDown={onComposerKey}
                  placeholder="Type your message…"
                  disabled={sending}
                  className="box-border max-h-28 min-h-11 flex-1 resize-none rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:border-green-600 focus:ring-2 focus:ring-green-100 disabled:bg-slate-50"
                />
                <button
                  type="submit"
                  disabled={!draft.trim() || sending}
                  aria-label="Send message"
                  className="flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-xl border-0 bg-green-700 text-white transition hover:bg-green-800 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {sending ? (
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                  ) : (
                    <Send size={17} />
                  )}
                </button>
              </form>
            </>
          )}

          <div className="border-t border-slate-100 bg-white px-4 py-2 text-center text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">
            Powered by GIGGS
          </div>
        </section>
      )}
    </>
  );
};

export default SupportChat;
